import React from 'react';
import { useSelector } from 'react-redux';
import { SyncLoader } from 'react-spinners';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { RootState } from '../../redux/store';
import { formatDateToISO, getSlotHHMM, formatTimeSlot, formatSlotToISO, getDayName, getDayNumber, isSlotInFuture } from '../../utils/helpers';
import { CalendarX2, Phone } from 'lucide-react';

interface ListViewProps {
  visibleDates: Date[];
  slotsByDate?: Record<string, Record<string, string[]>>;
  selectedSlot: string | null;
  onSlotSelect: (slotId: string) => void;
  onOperatoriesSelect?: (operatories: string[]) => void;
  bufferMinutes?: number;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  searchLimitReached?: boolean;
}

const ListView: React.FC<ListViewProps> = ({
  visibleDates,
  slotsByDate,
  selectedSlot,
  onSlotSelect,
  onOperatoriesSelect,
  bufferMinutes = 0,
  onLoadMore,
  isLoadingMore,
  searchLimitReached,
}) => {
  const practice = useSelector((s: RootState) => s.auth.practice);
  const timeZone = practice?.time_zone;

  const [sentryRef, { rootRef }] = useInfiniteScroll({
    loading: !!isLoadingMore,
    hasNextPage: !searchLimitReached,
    onLoadMore: onLoadMore || (() => { }),
    disabled: !!searchLimitReached,
    // Use a larger rootMargin to ensure loading starts well before hitting bottom
    rootMargin: '0px 0px 500px 0px',
  });

  if (searchLimitReached && visibleDates.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-6 py-8 text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
          <CalendarX2 className="h-8 w-8 text-gray-400" />
        </div>
        <h4 className="m-0 text-base font-semibold text-gray-900">
          No available slots
        </h4>
        <p className="m-0 mt-1 max-w-[280px] text-sm leading-5 text-gray-500">
          There are no open appointments for the upcoming 12 months. Please check
          back later or call us directly.
        </p>
        <a
          href={`tel:${practice?.phone_number}`}
          className="mt-4 inline-flex items-center gap-1.5 rounded-md border border-[#4A90E2] bg-white px-4 py-2 text-sm font-medium text-[#4A90E2] no-underline transition-all duration-200 hover:bg-[#4A90E2] hover:text-white"
        >
          <Phone className="h-3.5 w-3.5" />
          Call {practice?.phone_number}
        </a>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className="max-h-[370px] overflow-y-auto divide-y divide-gray-100"
    >
      {visibleDates.map((date, index) => {
        const dateStr = formatDateToISO(date);
        const timeSlotsObj = slotsByDate?.[dateStr] || {};
        const timeSlots = Object.keys(timeSlotsObj)
          .filter((slot) => isSlotInFuture(dateStr, slot, bufferMinutes, timeZone))
          .sort((a, b) => {
            const [ah, am] = getSlotHHMM(a).split(':').map((n) => parseInt(n || '0', 10));
            const [bh, bm] = getSlotHHMM(b).split(':').map((n) => parseInt(n || '0', 10));
            return ah * 60 + am - (bh * 60 + bm);
          });

        if (timeSlots.length === 0) {
          return null;
        }

        return (
          <div key={index} className="flex flex-col sm:flex-row gap-0 sm:gap-3 py-3 px-2">
            <div className="w-full sm:w-24 flex flex-shrink-0 items-center justify-center bg-black sm:bg-white rounded-lg sm:rounded-none sticky top-0 z-10">
              <div className="text-xs sm:text-sm font-semibold text-white sm:text-gray-900 py-2 sm:p-0">
                {getDayName(date)}, {date.toLocaleDateString('en-US', { month: 'short' })} {getDayNumber(date)}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 px-0 sm:px-2 pt-2 sm:pt-0">
              {timeSlots.map((slot) => {
                const slotId = formatSlotToISO(dateStr, slot);
                const isSelected = selectedSlot === slotId;
                const operatories_available = timeSlotsObj[slot];

                return (
                  <button
                    key={slot}
                    onClick={() => {
                      onSlotSelect(slotId);
                      if (onOperatoriesSelect) onOperatoriesSelect(operatories_available || []);
                    }}
                    className={`
                          px-2 py-3 text-xs rounded font-medium transition-all duration-200 border
                          ${isSelected ? 'bg-teamcare-yellow text-black border-teamcare-yellow' : 'bg-white text-gray-700 border-gray-200 hover:border-teamcare-yellow hover:text-teamcare-yellow'}
                        `}
                  >
                    {formatTimeSlot(slot)}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Sentinel element for Infinite Scroll Hook */}
      {!searchLimitReached && (
        <div ref={sentryRef} style={{ height: '20px', width: '100%', minHeight: '20px' }}>
          {isLoadingMore && (
            <div className="flex items-center justify-center py-4 bg-white w-full shadow-sm border-t border-gray-50">
              <span className="mr-3 text-xs sm:text-sm text-gray-500 font-medium">Searching more slots...</span>
              <SyncLoader size={6} color="#fbbf24" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ListView;
